import { BaseCounter } from './BaseCounter.js';
import { XGBoostPredictor, extractTemporalFeatures, TEMPORAL_WINDOW, StreamingDetectionPredictor, DEFAULT_MAX_ANCESTOR_DEPTH } from '../utils/xgboostPredictor.js';

/**
 * XGBoost Stroke Counter
 *
 * Uses a trained XGBoost model with temporal features (±7 frames) to detect strokes.
 * Runs entirely in the browser - no backend calls needed.
 */
export class XGBoostStrokeCounter extends BaseCounter {
  static get id() {
    return 'pickleball';
  }

  static get displayName() {
    return 'Pickleball Strokes (XGBoost ML)';
  }

  static get description() {
    return 'ML-based stroke detection using XGBoost with temporal features';
  }

  constructor(options = {}) {
    super();
    this.strokeCount = 0;

    // Alias repCount to strokeCount for base counter compatibility
    Object.defineProperty(this, 'repCount', {
      get: () => this.strokeCount,
      set: (val) => { this.strokeCount = val; }
    });

    // Options
    this.modelUrl = options.modelUrl || '/pickleball-model/xgboost-stroke-model.json';
    this.threshold = options.threshold || 0.5;
    this.frameW = options.frameW || 640;
    this.frameH = options.frameH || 480;

    // Temporal window settings (imported from xgboostPredictor.js)
    this.BUFFER_SIZE = TEMPORAL_WINDOW * 2 + 1; // 15 frames

    // Frame buffer for temporal features
    this.frameBuffer = [];
    // Parallel buffer for cached predictions (avoids re-processing all frames every update)
    this.predictionsBuffer = [];
    this.frameIndex = 0;

    // Detection predictor: tracks t-1 and t-2 predictions with ancestorDepth
    // Used for prediction chaining with efficient O(1) updates
    this.detectionPredictor = new StreamingDetectionPredictor(DEFAULT_MAX_ANCESTOR_DEPTH);

    // Stroke detection state - use FRAME-based debounce to match Python exactly
    // Python: MIN_FRAMES_BETWEEN_STROKES = 4, check: frames_since_last > 4
    // Initialize to -5 so first frame can be counted (0 - (-5) = 5 > 4)
    this.MIN_FRAMES_BETWEEN_STROKES = 4;
    this.lastStrokeFrameIndex = -this.MIN_FRAMES_BETWEEN_STROKES - 1;

    // XGBoost predictor
    this.predictor = new XGBoostPredictor();
    this.isModelLoaded = false;
    this.isLoading = false;
    this.loadError = null;

    // Debug info
    this.lastProbability = 0;
    this.predictionsThisSession = 0;

    // Model version info (populated from manifest during initialization)
    this.modelInfo = {
      xgboostModel: null,
      xgboostMeta: null
    };
  }

  /**
   * Initialize the model (call before using)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async initialize() {
    if (this.isModelLoaded) {
      return { success: true };
    }
    if (this.isLoading) {
      return { success: false, error: 'Model is still loading' };
    }

    this.isLoading = true;
    this.loadError = null;

    try {
      // Resolve model URL from manifest (supports content-addressed filenames)
      let actualModelUrl = this.modelUrl;
      const modelDir = this.modelUrl.replace(/\/[^/]+$/, '');

      try {
        const manifestUrl = `${modelDir}/manifest.xgboost.counter.json?_=${Date.now()}`;
        const manifestResp = await fetch(manifestUrl, { cache: 'no-store' });

        if (manifestResp.ok) {
          const manifest = await manifestResp.json();
          if (manifest.xgboostModel) {
            actualModelUrl = `${modelDir}/${manifest.xgboostModel}`;
            // Store model info for debugging/display
            this.modelInfo = {
              xgboostModel: manifest.xgboostModel,
              xgboostMeta: manifest.xgboostMeta || null
            };
          }
        }
      } catch (e) {
        // No manifest found, use default URL
      }

      const success = await this.predictor.load(actualModelUrl);
      this.isModelLoaded = success;

      if (success) {
        this.isLoading = false;
        return { success: true };
      } else {
        this.loadError = this.predictor.loadError || `Failed to load XGBoost model from ${actualModelUrl}`;
        console.error('XGBoostStrokeCounter:', this.loadError);
        this.isLoading = false;
        return { success: false, error: this.loadError };
      }
    } catch (error) {
      this.loadError = `Failed to load XGBoost model: ${error.message}`;
      console.error('XGBoostStrokeCounter:', this.loadError);
      this.isLoading = false;
      return { success: false, error: this.loadError };
    }
  }

  /**
   * Update the counter with frame data
   * @param {Object} frameData - {ball, paddle} format with x,y,width,height,confidence
   * @param {number} timestamp - Unused, kept for API compatibility
   * @param {Object} videoDimensions - Optional {width, height} of source video
   * @returns {number} Current stroke count
   */
  update(frameData, timestamp = null, videoDimensions = null) {
    // NOTE: timestamp parameter is ignored. The caller (UnifiedExerciseCounter)
    // controls frame rate via setInterval at 15fps. The counter just processes
    // every frame it receives and uses frameIndex for temporal features.

    if (!this.isModelLoaded) {
      // Try to initialize if not loaded
      if (!this.isLoading) {
        this.initialize();
      }
      return this.strokeCount;
    }

    // Update frame dimensions from video if provided
    if (videoDimensions && videoDimensions.width && videoDimensions.height) {
      if (this.frameW !== videoDimensions.width || this.frameH !== videoDimensions.height) {
        this.frameW = videoDimensions.width;
        this.frameH = videoDimensions.height;
      }
    }

    // frameData is already in {ball, paddle} format - use directly
    // This matches what extractTemporalFeatures/getPrimaryDetections expects
    const frame = frameData || {};
    this.frameBuffer.push(frame);

    // Process new frame through detection predictor and cache the prediction
    // This is O(1) per frame instead of O(buffer_size) per frame
    const prediction = this.detectionPredictor.processFrame(frame);
    this.predictionsBuffer.push({
      ball: prediction.ball,
      paddle: prediction.paddle,
      ballWasPredicted: prediction.ballWasPredicted,
      paddleWasPredicted: prediction.paddleWasPredicted
    });

    if (this.frameBuffer.length > this.BUFFER_SIZE) {
      // Shift out old frame and its cached prediction
      this.frameBuffer.shift();
      this.predictionsBuffer.shift();
    }
    this.frameIndex++;

    // Need full buffer for prediction
    if (this.frameBuffer.length < this.BUFFER_SIZE) {
      return this.strokeCount;
    }

    // Skip feature extraction and XGBoost prediction if disabled (for performance debugging)
    // This allows measuring pure YOLO inference overhead
    if (this.disablePrediction) {
      return this.strokeCount;
    }

    // Calculate the actual recording frame index being predicted
    // The prediction is for the CENTER of the buffer (TEMPORAL_WINDOW frames behind the latest)
    // frameIndex is 1-indexed (incremented after adding frame), so:
    // - After adding frame N, frameIndex = N+1
    // - Buffer contains frames [N-8, N-7, ..., N] (9 frames)
    // - Center frame is at buffer[4] = frame (N - 4)
    // - So predicted frame = (frameIndex - 1) - TEMPORAL_WINDOW = frameIndex - 5
    const predictedFrameIndex = this.frameIndex - 1 - TEMPORAL_WINDOW;

    // Extract features for the center frame using cached predictions
    const centerIdx = TEMPORAL_WINDOW; // Middle of buffer

    const features = extractTemporalFeatures(
      this.frameBuffer,
      centerIdx,
      this.frameW,
      this.frameH,
      { cachedPredictions: this.predictionsBuffer }
    );

    // Run XGBoost prediction (ML handles missing data via -1 sentinel values)
    const probability = this.predictor.predictProba(features);
    this.lastProbability = probability;
    this.predictionsThisSession++;

    // Check if stroke detected using RAW probability (not smoothed)
    // Smoothing was causing isolated high-probability frames to be averaged down below threshold
    const isStroke = probability >= this.threshold;

    // Use FRAME-based debounce to match Python exactly
    // Python: if frames_since_last > MIN_FRAMES_BETWEEN_STROKES (strictly greater than)
    // IMPORTANT: Use the actual predicted frame index, not the buffer frame index
    // This matches Python which uses the recording frame index for debouncing
    const framesSinceLastStroke = predictedFrameIndex - this.lastStrokeFrameIndex;

    if (isStroke && framesSinceLastStroke > this.MIN_FRAMES_BETWEEN_STROKES) {
      this.strokeCount++;
      this.lastStrokeFrameIndex = predictedFrameIndex;
    }

    return this.strokeCount;
  }

  /**
   * Finalize counting - process remaining frames in the buffer.
   *
   * This MUST be called after all frames have been processed to ensure
   * the last TEMPORAL_WINDOW frames are also predicted. Without this,
   * strokes in the final frames would be missed.
   *
   * This matches Python's behavior which predicts for ALL frames including
   * the last ones (using -1 sentinel for out-of-bounds context).
   *
   * @returns {number} Final stroke count
   */
  finalize() {
    if (!this.isModelLoaded || this.frameBuffer.length === 0) {
      return this.strokeCount;
    }

    // Process remaining frames by continuing to shift the buffer
    // and making predictions until all frames have been processed.
    // We pad with empty frames {} on the right side (which extractTemporalFeatures
    // handles via -1 sentinels, same as Python's out-of-bounds handling).
    const remainingPredictions = TEMPORAL_WINDOW;

    for (let i = 0; i < remainingPredictions; i++) {
      // Add an empty frame to shift the window forward
      const emptyFrame = {};
      this.frameBuffer.push(emptyFrame);

      // Process empty frame and cache prediction
      const prediction = this.detectionPredictor.processFrame(emptyFrame);
      this.predictionsBuffer.push({
        ball: prediction.ball,
        paddle: prediction.paddle,
        ballWasPredicted: prediction.ballWasPredicted,
        paddleWasPredicted: prediction.paddleWasPredicted
      });

      // Shift out old frame and prediction
      this.frameBuffer.shift();
      this.predictionsBuffer.shift();
      this.frameIndex++;

      // Calculate the predicted frame index
      const predictedFrameIndex = this.frameIndex - 1 - TEMPORAL_WINDOW;

      // Extract features using cached predictions
      const centerIdx = TEMPORAL_WINDOW;
      const features = extractTemporalFeatures(
        this.frameBuffer,
        centerIdx,
        this.frameW,
        this.frameH,
        { cachedPredictions: this.predictionsBuffer }
      );

      // Run XGBoost prediction
      const probability = this.predictor.predictProba(features);
      this.lastProbability = probability;
      this.predictionsThisSession++;

      // Check if stroke detected
      const isStroke = probability >= this.threshold;
      const framesSinceLastStroke = predictedFrameIndex - this.lastStrokeFrameIndex;

      if (isStroke && framesSinceLastStroke > this.MIN_FRAMES_BETWEEN_STROKES) {
        this.strokeCount++;
        this.lastStrokeFrameIndex = predictedFrameIndex;
      }
    }

    return this.strokeCount;
  }

  /**
   * Reset the counter
   */
  reset() {
    this.strokeCount = 0;
    this.frameBuffer = [];
    this.predictionsBuffer = [];
    this.frameIndex = 0;
    this.detectionPredictor.reset();
    // Initialize to allow first frame to be counted (same as Python)
    this.lastStrokeFrameIndex = -this.MIN_FRAMES_BETWEEN_STROKES - 1;
    this.lastProbability = 0;
    this.predictionsThisSession = 0;
  }

  /**
   * Get current state for debugging
   */
  getState() {
    // Calculate the actual predicted frame index (same logic as in update())
    const predictedFrameIndex = this.frameIndex >= this.BUFFER_SIZE
      ? this.frameIndex - 1 - TEMPORAL_WINDOW
      : -1;
    return {
      strokeCount: this.strokeCount,
      isModelLoaded: this.isModelLoaded,
      isLoading: this.isLoading,
      loadError: this.loadError,
      bufferSize: this.frameBuffer.length,
      frameIndex: this.frameIndex,
      predictedFrameIndex,
      lastProbability: this.lastProbability,
      predictionsThisSession: this.predictionsThisSession,
      threshold: this.threshold,
      framesSinceLastStroke: predictedFrameIndex - this.lastStrokeFrameIndex,
      modelInfo: this.modelInfo
    };
  }
}
